import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    Box, 
    Typography, 
    CircularProgress, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper,
    Alert,
    Tooltip,
    IconButton,
    Chip,
    TextField,
    InputAdornment,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
    Snackbar
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PaidIcon from '@mui/icons-material/Paid';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Api from '../../../Services/Api';
import { formatCurrency, formatNumber, hasLowPerformance, calculateRoas } from './utils';

/**
 * Campaign Detail Analysis Component
 * Shows detailed breakdown of a campaign by supply_feed_id
 * 
 * @param {Object} props - Component props
 * @param {string} props.subId - Campaign sub_id
 * @param {string} props.keyword - Campaign keyword
 * @param {Object} props.dateRange - Date range object with startDate and endDate
 * @param {Object} props.globalDateRange - Date range from parent component (if available)
 */
const CampaignDetailAnalysis = ({ subId, keyword, dateRange, globalDateRange }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [detailReports, setDetailReports] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [actualDateRange, setActualDateRange] = useState(null);
    const [sortConfig, setSortConfig] = useState({
        key: 'revenue',
        direction: 'desc'
    });
    const [blacklistDialogOpen, setBlacklistDialogOpen] = useState(false);
    const [feedToBlacklist, setFeedToBlacklist] = useState(null);
    const [blacklistAction, setBlacklistAction] = useState('add'); // 'add' or 'remove'
    const [blacklisting, setBlacklisting] = useState(false);
    const [blacklistStatuses, setBlacklistStatuses] = useState({});
    const [checkingBlacklist, setCheckingBlacklist] = useState(false);
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'info'
    });

    // Log the received date ranges for debugging
    useEffect(() => {
        console.debug('CampaignDetailAnalysis received dateRange:', dateRange);
        console.debug('CampaignDetailAnalysis received globalDateRange:', globalDateRange);
    }, [dateRange, globalDateRange]);

    // Normalize the date range to ensure we always have valid dates
    const normalizedDateRange = useMemo(() => {
        // First priority: Use the dateRange prop if it's valid
        if (dateRange && dateRange.startDate && dateRange.endDate) {
            console.debug('Using dateRange prop');
            return {
                startDate: new Date(dateRange.startDate),
                endDate: new Date(dateRange.endDate)
            };
        }
        
        // Second priority: Use the globalDateRange prop if it's valid
        if (globalDateRange && globalDateRange.startDate && globalDateRange.endDate) {
            console.debug('Using globalDateRange prop');
            return {
                startDate: new Date(globalDateRange.startDate),
                endDate: new Date(globalDateRange.endDate)
            };
        }
        
        // Third priority: Try to get the date range from window.appState if available
        if (window.appState && window.appState.dateRange && 
            window.appState.dateRange.startDate && window.appState.dateRange.endDate) {
            console.debug('Using window.appState.dateRange');
            return {
                startDate: new Date(window.appState.dateRange.startDate),
                endDate: new Date(window.appState.dateRange.endDate)
            };
        }
        
        // Last resort: Use last 30 days
        console.debug('Using fallback date range (last 30 days)');
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);
        
        return { startDate, endDate };
    }, [dateRange, globalDateRange]);

    // Fetch detailed reports grouped by supply_feed_id
    const fetchDetailReports = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Use the normalized date range
            const { startDate, endDate } = normalizedDateRange;
            
            console.debug('CampaignDetailAnalysis fetchDetailReports using date range:', {
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
                startDateObj: startDate,
                endDateObj: endDate
            });
            
            // Store the actual date range used for display
            setActualDateRange({ startDate, endDate });
            
            // Create filters for the specific sub_id and keyword
            const filters = [];
            if (subId) {
                filters.push({
                    field: 'sub_id',
                    operator: '=',
                    value: subId
                });
            }
            if (keyword) {
                filters.push({
                    field: 'keyword',
                    operator: '=',
                    value: keyword
                });
            }
            
            console.debug('Fetching detailed reports with:', {
                dateRange: { startDate, endDate },
                groupBy: 'supply_feed_id',
                filters
            });
            
            // Call the API with supply_feed_id as the groupBy parameter
            const response = await Api.getMediabuyingReports(
                { startDate, endDate },
                'supply_feed_id',
                filters
            );
            
            console.debug('Received detailed reports:', response.length);
            
            // Aggregate data by supply_feed_id across all dates
            const aggregatedData = aggregateBySupplyFeedId(response);
            
            setDetailReports(aggregatedData);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching detailed reports:', err);
            setError('Failed to load detailed campaign analysis. Please try again.');
            setLoading(false);
        }
    }, [subId, keyword, normalizedDateRange]);

    // Aggregate data by supply_feed_id across all dates
    const aggregateBySupplyFeedId = (data) => {
        // Create a map to store aggregated data by supply_feed_id
        const aggregatedMap = new Map();
        
        // Process each report
        data.forEach(report => {
            const supplyFeedId = report.supply_feed_id || 'unknown';
            
            // If this supply_feed_id is not in the map yet, initialize it
            if (!aggregatedMap.has(supplyFeedId)) {
                aggregatedMap.set(supplyFeedId, {
                    supply_feed_id: supplyFeedId,
                    feed_name: report.feed_name || 'Unknown Feed',
                    clicks: 0,
                    conversions: 0,
                    cost: 0,
                    revenue: 0,
                    total_revenue: 0,
                    taboola_clicks: 0,
                    tq: 0,
                    tq_sum: 0,
                    tq_count: 0,
                    payout: 0, // Initialize payout
                    // We'll calculate these derived metrics after aggregation
                    cpc: 0,
                    cvr: 0,
                    roas: 0,
                    coverage: 0,
                    // Track the number of days for averaging
                    dayCount: 0,
                    // Initialize searches and hits for coverage calculation
                    searches: 0,
                    hits: 0,
                    // Track dates for this feed
                    dates: []
                });
            }
            
            // Get the current aggregated data for this supply_feed_id
            const aggregated = aggregatedMap.get(supplyFeedId);
            
            // Add this report's data to the aggregated data
            aggregated.clicks += parseFloat(report.clicks) || 0;
            aggregated.conversions += parseFloat(report.conversions) || 0;
            aggregated.cost += parseFloat(report.cost) || 0;
            aggregated.revenue += parseFloat(report.revenue) || 0;
            aggregated.total_revenue += parseFloat(report.total_revenue) || 0;
            aggregated.taboola_clicks += parseFloat(report.taboola_clicks) || 0;
            
            // Sum up searches and hits for coverage calculation
            aggregated.searches += parseFloat(report.searches) || 0;
            aggregated.hits += parseFloat(report.hits) || 0;
            
            // Add date if not already included
            if (!aggregated.dates.includes(report.date)) {
                aggregated.dates.push(report.date);
            }
            
            // For TQ, we'll take the most recent date's data (excluding today)
            const reportDate = new Date(report.date);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            // Only update TQ if this report is not from today
            if (reportDate < today) {
                const reportTqSum = parseFloat(report.tq_sum) || 0;
                const reportTqCount = parseFloat(report.tq_count) || 0;
                
                // If this report has valid TQ data and is more recent than what we have
                if (reportTqCount > 0 && (!aggregated.tq_count || reportDate > new Date(aggregated.dates[0]))) {
                    aggregated.tq_sum = reportTqSum;
                    aggregated.tq_count = reportTqCount;
                    aggregated.tq = reportTqCount > 0 ? Math.round(reportTqSum / reportTqCount) : 0;
                }
            }
            
            // Increment day count
            aggregated.dayCount += 1;
        });
        
        // Convert the map to an array and calculate derived metrics
        const aggregatedArray = Array.from(aggregatedMap.values()).map(item => {
            // Calculate CPC directly as cost/taboola_clicks
            const taboola_clicks = parseFloat(item.taboola_clicks) || 0;
            const cpc = taboola_clicks > 0 ? item.cost / taboola_clicks : 0;
            
            // Calculate CVR directly as (conversions/taboola_clicks)*100
            const conversions = parseFloat(item.conversions) || 0;
            const cvr = taboola_clicks > 0 ? (conversions / taboola_clicks) * 100 : 0;
            
            // Calculate ROAS as revenue/cost (simpler and more standard formula)
            // NOTE: This uses revenue (not total_revenue) for ROAS calculation
            const revenue = parseFloat(item.revenue) || 0;
            const cost = parseFloat(item.cost) || 0;
            const roas = cost > 0 ? revenue / cost : 0;
            
            // Calculate coverage directly from summed searches and hits
            const searches = parseFloat(item.searches) || 0;
            const hits = parseFloat(item.hits) || 0;
            const coverage = hits > 0 ? (searches / hits) * 100 : 0;
            
            // Calculate payout as total_revenue/sum(clicks)
            // This represents the average revenue generated per click across the entire period
            // IMPORTANT: We calculate this directly from total_revenue and clicks, NOT from any pre-calculated payout value
            const payout = item.clicks > 0 ? item.total_revenue / item.clicks : 0;
            
            // Sort dates to ensure we have the most recent first
            item.dates.sort((a, b) => new Date(b) - new Date(a));
            
            return {
                ...item,
                cpc,
                cvr,
                roas,
                payout,
                coverage
            };
        });
        
        return aggregatedArray;
    };

    // Fetch data when component mounts or when dependencies change
    useEffect(() => {
        console.debug('Dependencies changed, fetching data with:', {
            subId,
            keyword,
            dateRange: normalizedDateRange
        });
        fetchDetailReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subId, keyword, normalizedDateRange]);

    // Sort the reports based on sortConfig
    const sortedReports = React.useMemo(() => {
        let sortableReports = [...detailReports];
        
        if (sortConfig.key) {
            sortableReports.sort((a, b) => {
                // Handle numeric values - ensure proper type conversion
                let aValue, bValue;
                
                // Special handling for string fields
                if (sortConfig.key === 'supply_feed_id') {
                    aValue = (a[sortConfig.key] || '').toString().toLowerCase();
                    bValue = (b[sortConfig.key] || '').toString().toLowerCase();
                    
                    // For string comparison
                    if (aValue < bValue) {
                        return sortConfig.direction === 'asc' ? -1 : 1;
                    }
                    if (aValue > bValue) {
                        return sortConfig.direction === 'asc' ? 1 : -1;
                    }
                    return 0;
                } 
                // Special handling for payout - calculate it directly
                else if (sortConfig.key === 'payout') {
                    aValue = a.clicks > 0 ? a.total_revenue / a.clicks : 0;
                    bValue = b.clicks > 0 ? b.total_revenue / b.clicks : 0;
                    
                    // For numeric comparison
                    if (aValue < bValue) {
                        return sortConfig.direction === 'asc' ? -1 : 1;
                    }
                    if (aValue > bValue) {
                        return sortConfig.direction === 'asc' ? 1 : -1;
                    }
                    return 0;
                }
                else {
                    // For numeric fields, ensure proper conversion
                    aValue = parseFloat(a[sortConfig.key]) || 0;
                    bValue = parseFloat(b[sortConfig.key]) || 0;
                    
                    // For numeric comparison
                    if (aValue < bValue) {
                        return sortConfig.direction === 'asc' ? -1 : 1;
                    }
                    if (aValue > bValue) {
                        return sortConfig.direction === 'asc' ? 1 : -1;
                    }
                    return 0;
                }
            });
        }
        return sortableReports;
    }, [detailReports, sortConfig]);

    // Filter reports based on search term
    const filteredReports = React.useMemo(() => {
        if (!searchTerm.trim()) {
            return sortedReports;
        }
        
        const searchTermLower = searchTerm.toLowerCase().trim();
        return sortedReports.filter(report => {
            const supplyFeedId = (report.supply_feed_id || '').toString().toLowerCase();
            const feedName = (report.feed_name || '').toString().toLowerCase();
            
            return supplyFeedId.includes(searchTermLower) || feedName.includes(searchTermLower);
        });
    }, [sortedReports, searchTerm]);

    // Handle column header click for sorting
    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Handle search input change
    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    // Get sort direction indicator
    const getSortDirectionIndicator = (key) => {
        if (sortConfig.key !== key) {
            return null;
        }
        return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    };

    // Calculate thresholds for highlighting high TQ and best payout
    const calculateThresholds = () => {
        if (!detailReports || detailReports.length === 0) return { 
            tqThreshold: 0, 
            maxTqThreshold: 0,
            roasThreshold: 0, 
            payoutThreshold: 0,
            maxPayoutThreshold: 0,
            hasValidPayoutData: false
        };
        
        // Check if we have any valid payout data (reports with clicks > 0)
        const reportsWithClicks = detailReports.filter(report => report.clicks > 0);
        const hasValidPayoutData = reportsWithClicks.length > 0;
        
        // Sort reports by TQ and payout to find top performers
        const sortedByTQ = [...detailReports].sort((a, b) => (b.tq || 0) - (a.tq || 0));
        const sortedByROAS = [...detailReports].sort((a, b) => (b.roas || 0) - (a.roas || 0));
        
        // Get top 25% threshold for TQ (good)
        const tqThreshold = sortedByTQ[Math.floor(sortedByTQ.length * 0.25)]?.tq || 0;
        
        // For Max TQ, use a more restrictive approach:
        // - Either take top 3 feeds or top 5% (whichever is smaller)
        // - But ensure we don't take more than 3 feeds for small datasets
        const maxTqIndex = Math.min(2, Math.floor(sortedByTQ.length * 0.05));
        const maxTqThreshold = sortedByTQ[maxTqIndex]?.tq || 0;
        
        // Get top 25% threshold for ROAS
        const roasThreshold = sortedByROAS[Math.floor(sortedByROAS.length * 0.25)]?.roas || 0;
        
        // Calculate payouts for all feeds with clicks
        const feedsWithPayouts = reportsWithClicks.map(report => ({
            supply_feed_id: report.supply_feed_id,
            payout: report.total_revenue / report.clicks
        }));
        
        // Sort by payout (highest to lowest)
        const sortedByPayout = [...feedsWithPayouts]
            .sort((a, b) => b.payout - a.payout);
        
        // Log the top payouts for debugging
        console.debug('Top 5 payouts:', sortedByPayout.slice(0, 5));
        
        // Get top 25% threshold for payout (good)
        // Make sure we have at least one feed with good payout
        const payoutThresholdIndex = Math.min(
            Math.floor(sortedByPayout.length * 0.25),
            Math.floor(sortedByPayout.length * 0.5) // Ensure at least half of feeds can qualify
        );
        
        const payoutThreshold = sortedByPayout.length > 0 ? 
            sortedByPayout[payoutThresholdIndex]?.payout || 0 : 0;
        
        // For Max Payout, use a more restrictive approach:
        // - Either take top 3 feeds or top 5% (whichever is smaller)
        // - But ensure we don't take more than 3 feeds for small datasets
        const maxPayoutIndex = Math.min(2, Math.floor(sortedByPayout.length * 0.05));
        const maxPayoutThreshold = sortedByPayout.length > 0 ?
            sortedByPayout[maxPayoutIndex]?.payout || 0 : 0;
        
        console.debug('Threshold calculation:', {
            totalReports: detailReports.length,
            reportsWithClicks: reportsWithClicks.length,
            hasValidPayoutData,
            payoutThreshold,
            maxPayoutThreshold,
            topPayouts: sortedByPayout.slice(0, 3).map(f => ({ 
                id: f.supply_feed_id, 
                payout: f.payout 
            }))
        });
        
        return { 
            tqThreshold, 
            maxTqThreshold, 
            roasThreshold, 
            payoutThreshold, 
            maxPayoutThreshold,
            hasValidPayoutData
        };
    };
    
    const { 
        tqThreshold, 
        maxTqThreshold, 
        roasThreshold, 
        payoutThreshold, 
        maxPayoutThreshold,
        hasValidPayoutData
    } = calculateThresholds();

    // Helper function to determine if a feed has max TQ
    const hasMaxTQ = (report) => {
        // Only consider feeds with positive TQ values
        if (report.tq <= 0) return false;
        
        // Find the feed with the highest TQ
        const sortedByTQ = [...detailReports]
            .sort((a, b) => (b.tq || 0) - (a.tq || 0));
        
        // Return true only if this is the single feed with the highest TQ
        // In case of a tie, only the first one in the array gets the Max label
        return sortedByTQ.length > 0 && 
               sortedByTQ[0].supply_feed_id === report.supply_feed_id && 
               sortedByTQ[0].tq === report.tq;
    };
    
    // Helper function to determine if a feed has max payout
    const hasMaxPayout = (report) => {
        // If we don't have any valid payout data, return false
        if (!hasValidPayoutData) {
            console.debug('hasMaxPayout: No valid payout data available');
            return false;
        }
        
        // Only consider feeds with positive clicks
        if (!report.clicks || report.clicks <= 0) {
            console.debug(`hasMaxPayout: Feed ${report.supply_feed_id} has no clicks`);
            return false;
        }
        
        // Calculate payout directly
        const calculatedPayout = report.total_revenue / report.clicks;
        
        // Only consider feeds with significant payout values (at least $0.10)
        if (calculatedPayout < 0.10) {
            console.debug(`hasMaxPayout: Feed ${report.supply_feed_id} has low payout: ${calculatedPayout}`);
            return false;
        }
        
        // Find all feeds with positive clicks
        const feedsWithClicks = detailReports.filter(r => r.clicks > 0);
        
        // If there are no feeds with clicks, return false
        if (feedsWithClicks.length === 0) {
            console.debug('hasMaxPayout: No feeds with clicks found');
            return false;
        }
        
        // Calculate payouts for all feeds
        const feedsWithPayouts = feedsWithClicks.map(r => ({
            supply_feed_id: r.supply_feed_id,
            payout: r.total_revenue / r.clicks
        }));
        
        // Sort feeds by payout (highest to lowest)
        const sortedByPayout = [...feedsWithPayouts]
            .sort((a, b) => b.payout - a.payout);
        
        // Get the highest payout value
        const highestPayout = sortedByPayout[0].payout;
        
        // Check if this feed has the highest payout (within a small margin for floating point precision)
        const isMaxPayout = Math.abs(calculatedPayout - highestPayout) < 0.001;
        
        // Debug logging to help diagnose the issue
        if (isMaxPayout || report.supply_feed_id === sortedByPayout[0].supply_feed_id) {
            console.debug('hasMaxPayout check for potential max feed:', {
                feedId: report.supply_feed_id,
                calculatedPayout,
                highestPayout,
                isHighest: isMaxPayout,
                topFeedId: sortedByPayout[0].supply_feed_id,
                difference: Math.abs(calculatedPayout - highestPayout),
                allTopFeeds: sortedByPayout.slice(0, 3)
            });
        }
        
        return isMaxPayout;
    };

    // Format date for display with better readability
    const formatDate = (date) => {
        if (!date) return '';
        
        // Check if date is already a Date object, if not convert it
        const dateObj = date instanceof Date ? date : new Date(date);
        
        // Format as MMM DD, YYYY (e.g., Jan 01, 2023)
        return dateObj.toLocaleDateString('en-US', {
            month: 'short',
            day: '2-digit',
            year: 'numeric'
        });
    };

    // Get a user-friendly date range string
    const getDateRangeString = () => {
        if (!actualDateRange) return 'Loading date range...';
        
        const { startDate, endDate } = actualDateRange;
        return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    };

    // Add this after the filteredReports is defined
    // Debug logging for payout labels - log all reports
    useEffect(() => {
        if (filteredReports.length > 0) {
            console.debug('PAYOUT LABELS DEBUG - All reports:', filteredReports.map(report => {
                const calculatedPayout = report.clicks > 0 ? report.total_revenue / report.clicks : 0;
                const isMaxPayout = hasMaxPayout(report);
                const isGoodPayout = hasValidPayoutData && 
                                    report.clicks > 0 && 
                                    calculatedPayout >= payoutThreshold && 
                                    calculatedPayout >= 0.10 && 
                                    !isMaxPayout;
                
                return {
                    supply_feed_id: report.supply_feed_id,
                    clicks: report.clicks,
                    total_revenue: report.total_revenue,
                    calculatedPayout,
                    payoutThreshold,
                    isMaxPayout,
                    isGoodPayout,
                    hasValidPayoutData
                };
            }));

            // Log the top payout feed
            const sortedByPayout = [...filteredReports]
                .filter(r => r.clicks > 0)
                .sort((a, b) => ((b.total_revenue / b.clicks) || 0) - ((a.total_revenue / a.clicks) || 0));
            
            if (sortedByPayout.length > 0) {
                console.debug('PAYOUT LABELS DEBUG - Top payout feed:', {
                    supply_feed_id: sortedByPayout[0].supply_feed_id,
                    clicks: sortedByPayout[0].clicks,
                    total_revenue: sortedByPayout[0].total_revenue,
                    calculatedPayout: sortedByPayout[0].total_revenue / sortedByPayout[0].clicks,
                    payoutThreshold,
                    isMaxPayout: hasMaxPayout(sortedByPayout[0]),
                    numberOfMaxPayoutFeeds: filteredReports.filter(r => hasMaxPayout(r)).length,
                    hasValidPayoutData
                });
            } else {
                console.debug('PAYOUT LABELS DEBUG - No reports with clicks > 0 found');
            }
        }
    }, [filteredReports, payoutThreshold, hasValidPayoutData]);

    // Handle opening the blacklist dialog
    const handleOpenBlacklistDialog = (report, action) => {
        setFeedToBlacklist(report);
        setBlacklistAction(action);
        setBlacklistDialogOpen(true);
    };

    // Handle closing the blacklist dialog
    const handleCloseBlacklistDialog = () => {
        setBlacklistDialogOpen(false);
        setFeedToBlacklist(null);
    };

    // Handle blacklisting or removing from blacklist
    const handleBlacklistAction = async () => {
        if (!feedToBlacklist || !subId) return;
        
        try {
            setBlacklisting(true);
            
            if (blacklistAction === 'add') {
                // Add to blacklist
                await Api.addToBlacklist({
                    supply_feed_id: feedToBlacklist.supply_feed_id,
                    sub_id: subId
                });
                
                setNotification({
                    open: true,
                    message: `Supply Feed ID ${feedToBlacklist.supply_feed_id} has been blacklisted for sub_id ${subId}`,
                    severity: 'success'
                });
            } else {
                // Find the blacklist entry ID
                const response = await Api.getBlacklist({
                    supply_feed_id: feedToBlacklist.supply_feed_id,
                    sub_id: subId
                });
                
                if (response.data && response.data.length > 0) {
                    // Remove from blacklist
                    await Api.removeFromBlacklist({
                        id: response.data[0].id
                    });
                    
                    setNotification({
                        open: true,
                        message: `Supply Feed ID ${feedToBlacklist.supply_feed_id} has been removed from blacklist for sub_id ${subId}`,
                        severity: 'success'
                    });
                } else {
                    throw new Error('Blacklist entry not found');
                }
            }
            
            // Update blacklist statuses
            const updatedStatuses = { ...blacklistStatuses };
            updatedStatuses[feedToBlacklist.supply_feed_id] = blacklistAction === 'add';
            setBlacklistStatuses(updatedStatuses);
            
            // Close the dialog
            setBlacklistDialogOpen(false);
            setFeedToBlacklist(null);
            setBlacklisting(false);
            
        } catch (err) {
            console.error('Error performing blacklist action:', err);
            
            let errorMessage = `Failed to ${blacklistAction === 'add' ? 'blacklist' : 'remove from blacklist'} supply feed. Please try again.`;
            
            // Check for specific error responses
            if (err.response && err.response.status === 409) {
                errorMessage = 'This combination is already blacklisted.';
            } else if (err.response && err.response.data && err.response.data.messages) {
                errorMessage = Object.values(err.response.data.messages).flat().join(' ');
            }
            
            setNotification({
                open: true,
                message: errorMessage,
                severity: 'error'
            });
            
            setBlacklisting(false);
        }
    };

    // Handle closing the notification
    const handleCloseNotification = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        
        setNotification({
            ...notification,
            open: false
        });
    };

    // Check blacklist status for all supply feeds
    const checkBlacklistStatuses = useCallback(async () => {
        if (!subId || !detailReports.length) return;
        
        try {
            setCheckingBlacklist(true);
            const statuses = {};
            
            // Create an array of promises for checking each feed
            const checkPromises = detailReports.map(async (report) => {
                try {
                    const response = await Api.checkBlacklistStatus({
                        supply_feed_id: report.supply_feed_id,
                        sub_id: subId
                    });
                    
                    statuses[report.supply_feed_id] = response.is_blacklisted;
                } catch (err) {
                    console.error(`Error checking blacklist status for feed ${report.supply_feed_id}:`, err);
                    statuses[report.supply_feed_id] = false;
                }
            });
            
            // Wait for all checks to complete
            await Promise.all(checkPromises);
            
            setBlacklistStatuses(statuses);
            setCheckingBlacklist(false);
            
            console.debug('Blacklist statuses:', statuses);
        } catch (err) {
            console.error('Error checking blacklist statuses:', err);
            setCheckingBlacklist(false);
        }
    }, [subId, detailReports]);
    
    // Check blacklist statuses when detail reports change
    useEffect(() => {
        if (subId && detailReports.length > 0) {
            checkBlacklistStatuses();
        }
    }, [subId, detailReports, checkBlacklistStatuses]);

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" component="h2" sx={{ color: 'white', mb: 1 }}>
                    Supply Feed Breakdown
                </Typography>
                <Tooltip title="This analysis shows how this campaign performs across different supply feeds, aggregated across the selected date range">
                    <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        <InfoIcon />
                    </IconButton>
                </Tooltip>
            </Box>
            
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress sx={{ color: 'white' }} />
                </Box>
            ) : error ? (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            ) : sortedReports.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>No detailed data available for this campaign.</Alert>
            ) : (
                <>
                    {/* Date range display */}
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                        <CalendarTodayIcon sx={{ color: 'rgba(255,255,255,0.7)', mr: 1, fontSize: '1rem' }} />
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            Data from: <strong>{getDateRangeString()}</strong>
                        </Typography>
                    </Box>
                    
                    {/* Search input field */}
                    <Box sx={{ mb: 2 }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search by Supply Feed ID or Name..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    color: 'white',
                                    bgcolor: 'rgba(255,255,255,0.05)',
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.1)',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255,255,255,0.2)',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255,255,255,0.3)',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'primary.main',
                                    },
                                }
                            }}
                        />
                    </Box>
                    
                    {/* Results count */}
                    {searchTerm.trim() && (
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                Found {filteredReports.length} {filteredReports.length === 1 ? 'result' : 'results'} for "{searchTerm}"
                            </Typography>
                        </Box>
                    )}
                    
                    <TableContainer 
                        component={Paper} 
                        elevation={3} 
                        sx={{ 
                            borderRadius: '8px', 
                            bgcolor: '#1e2130',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            maxHeight: '500px',
                            overflow: 'auto',
                            // Custom scrollbar styling
                            '&::-webkit-scrollbar': {
                                width: '6px',
                                height: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: 'rgba(0,0,0,0.1)',
                                borderRadius: '8px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                borderRadius: '8px',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.3)',
                                },
                            },
                            // For Firefox
                            scrollbarWidth: 'thin',
                            scrollbarColor: 'rgba(255,255,255,0.2) rgba(0,0,0,0.1)',
                        }}
                    >
                        <Table stickyHeader size="small" sx={{ borderCollapse: 'collapse' }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell 
                                        sx={{ 
                                            bgcolor: '#2c3040', 
                                            color: 'white',
                                            cursor: 'pointer',
                                            '&:hover': { opacity: 0.8 },
                                            padding: '4px 8px',
                                            fontSize: '0.85rem'
                                        }}
                                        onClick={() => requestSort('supply_feed_id')}
                                    >
                                        <strong>Supply Feed ID{getSortDirectionIndicator('supply_feed_id')}</strong>
                                    </TableCell>
                                    <TableCell 
                                        sx={{ 
                                            bgcolor: '#2c3040', 
                                            color: 'white',
                                            cursor: 'pointer',
                                            '&:hover': { opacity: 0.8 },
                                            padding: '4px 8px',
                                            fontSize: '0.85rem'
                                        }}
                                        onClick={() => requestSort('tq')}
                                    >
                                        <strong>TQ{getSortDirectionIndicator('tq')}</strong>
                                    </TableCell>
                                    <TableCell 
                                        sx={{ 
                                            bgcolor: '#2c3040', 
                                            color: 'white',
                                            cursor: 'pointer',
                                            '&:hover': { opacity: 0.8 },
                                            padding: '4px 8px',
                                            fontSize: '0.85rem'
                                        }}
                                        onClick={() => requestSort('taboola_clicks')}
                                    >
                                        <strong>Searches{getSortDirectionIndicator('taboola_clicks')}</strong>
                                    </TableCell>
                                    <TableCell 
                                        sx={{ 
                                            bgcolor: '#2c3040', 
                                            color: 'white',
                                            cursor: 'pointer',
                                            '&:hover': { opacity: 0.8 },
                                            padding: '4px 8px',
                                            fontSize: '0.85rem'
                                        }}
                                        onClick={() => requestSort('conversions')}
                                    >
                                        <strong>Clicks{getSortDirectionIndicator('conversions')}</strong>
                                    </TableCell>
                                    <TableCell 
                                        sx={{ 
                                            bgcolor: '#2c3040', 
                                            color: 'white',
                                            cursor: 'pointer',
                                            '&:hover': { opacity: 0.8 },
                                            padding: '4px 8px',
                                            fontSize: '0.85rem'
                                        }}
                                        onClick={() => requestSort('cvr')}
                                    >
                                        <strong>CVR (%){getSortDirectionIndicator('cvr')}</strong>
                                    </TableCell>
                                    <TableCell 
                                        sx={{ 
                                            bgcolor: '#2c3040', 
                                            color: 'white',
                                            cursor: 'pointer',
                                            '&:hover': { opacity: 0.8 },
                                            padding: '4px 8px',
                                            fontSize: '0.85rem'
                                        }}
                                        onClick={() => requestSort('coverage')}
                                    >
                                        <strong>Coverage (%){getSortDirectionIndicator('coverage')}</strong>
                                    </TableCell>
                                    <TableCell 
                                        sx={{ 
                                            bgcolor: '#2c3040', 
                                            color: 'white',
                                            cursor: 'pointer',
                                            '&:hover': { opacity: 0.8 },
                                            padding: '4px 8px',
                                            fontSize: '0.85rem'
                                        }}
                                        onClick={() => requestSort('cost')}
                                    >
                                        <strong>Cost{getSortDirectionIndicator('cost')}</strong>
                                    </TableCell>
                                    <TableCell 
                                        sx={{ 
                                            bgcolor: '#2c3040', 
                                            color: 'white',
                                            cursor: 'pointer',
                                            '&:hover': { opacity: 0.8 },
                                            padding: '4px 8px',
                                            fontSize: '0.85rem'
                                        }}
                                        onClick={() => requestSort('cpc')}
                                    >
                                        <strong>CPC{getSortDirectionIndicator('cpc')}</strong>
                                    </TableCell>
                                    <TableCell 
                                        sx={{ 
                                            bgcolor: '#2c3040', 
                                            color: 'white',
                                            cursor: 'pointer',
                                            '&:hover': { opacity: 0.8 },
                                            padding: '4px 8px',
                                            fontSize: '0.85rem'
                                        }}
                                        onClick={() => requestSort('revenue')}
                                    >
                                        <strong>Revenue{getSortDirectionIndicator('revenue')}</strong>
                                    </TableCell>
                                    <TableCell 
                                        sx={{ 
                                            bgcolor: '#2c3040', 
                                            color: 'white',
                                            cursor: 'pointer',
                                            '&:hover': { opacity: 0.8 },
                                            padding: '4px 8px',
                                            fontSize: '0.85rem'
                                        }}
                                        onClick={() => requestSort('payout')}
                                    >
                                        <Tooltip title="Payout is calculated as Revenue divided by Clicks (Revenue per Click)">
                                            <Box component="span">
                                                <strong>Payout{getSortDirectionIndicator('payout')}</strong>
                                            </Box>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell 
                                        sx={{ 
                                            bgcolor: '#2c3040', 
                                            color: 'white',
                                            cursor: 'pointer',
                                            '&:hover': { opacity: 0.8 },
                                            padding: '4px 8px',
                                            fontSize: '0.85rem'
                                        }}
                                        onClick={() => requestSort('roas')}
                                    >
                                        <strong>ROAS{getSortDirectionIndicator('roas')}</strong>
                                    </TableCell>
                                    <TableCell 
                                        sx={{ 
                                            bgcolor: '#2c3040', 
                                            color: 'white',
                                            width: '80px',
                                            textAlign: 'center',
                                            padding: '4px 8px',
                                            fontSize: '0.85rem'
                                        }}
                                    >
                                        <strong>Actions</strong>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredReports.map((report) => {
                                    const isLowPerformance = hasLowPerformance(report);
                                    const isGoodTQ = report.tq >= tqThreshold && report.tq > 0 && !hasMaxTQ(report);
                                    const isMaxTQ = hasMaxTQ(report);
                                    const isHighROAS = report.roas >= roasThreshold && report.roas > 1;
                                    
                                    // Calculate payout directly
                                    const calculatedPayout = report.clicks > 0 ? report.total_revenue / report.clicks : 0;
                                    
                                    // Determine if this feed has max payout
                                    const isMaxPayout = hasMaxPayout(report);
                                    
                                    // A feed has good payout if:
                                    // 1. We have valid payout data
                                    // 2. It has clicks
                                    // 3. Its payout is at least the threshold value
                                    // 4. Its payout is at least $0.10
                                    // 5. It's not already labeled as max payout
                                    const isGoodPayout = hasValidPayoutData && 
                                                       report.clicks > 0 && 
                                                       calculatedPayout >= payoutThreshold && 
                                                       calculatedPayout >= 0.10 && 
                                                       !isMaxPayout;
                                    
                                    // Debug logging for payout labels - log all feeds
                                    console.debug(`Feed ${report.supply_feed_id} payout details:`, {
                                        clicks: report.clicks,
                                        total_revenue: report.total_revenue,
                                        calculatedPayout,
                                        payoutThreshold,
                                        hasValidPayoutData,
                                        isMaxPayout,
                                        isGoodPayout,
                                        meetsThreshold: calculatedPayout >= payoutThreshold,
                                        meetsMinimum: calculatedPayout >= 0.10
                                    });
                                    
                                    // Debug logging for payout labels
                                    if (Math.random() < 0.1) { // Only log a small percentage to avoid flooding the console
                                        console.debug('Payout Labels Check:', {
                                            feedId: report.supply_feed_id,
                                            calculatedPayout,
                                            payoutThreshold,
                                            isGoodPayout,
                                            isMaxPayout,
                                            meetsThreshold: calculatedPayout >= payoutThreshold,
                                            meetsMinimum: calculatedPayout >= 0.10,
                                            hasClicks: report.clicks > 0,
                                            notMaxPayout: !isMaxPayout,
                                            hasValidPayoutData
                                        });
                                    }
                                    
                                    return (
                                        <TableRow 
                                            key={report.supply_feed_id}
                                            sx={{
                                                bgcolor: isLowPerformance 
                                                    ? 'rgba(244, 67, 54, 0.15)' 
                                                    : isMaxPayout
                                                    ? 'rgba(76, 175, 80, 0.15)'
                                                    : blacklistStatuses[report.supply_feed_id]
                                                    ? 'rgba(0, 0, 0, 0.25)'
                                                    : '#1e2130',
                                                '&:hover': { bgcolor: '#2c3040' },
                                                color: 'white',
                                                borderLeft: isLowPerformance 
                                                    ? '4px solid #f44336' 
                                                    : isMaxPayout 
                                                    ? '4px solid #4caf50' 
                                                    : blacklistStatuses[report.supply_feed_id]
                                                    ? '4px solid #9e9e9e'
                                                    : 'none',
                                                opacity: blacklistStatuses[report.supply_feed_id] ? 0.7 : 1,
                                                height: '32px'
                                            }}
                                        >
                                            <TableCell sx={{ 
                                                color: 'white',
                                                padding: '2px 8px',
                                                fontSize: '0.85rem',
                                                borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                                            }}>
                                                {report.supply_feed_id || 'Unknown'}
                                            </TableCell>
                                            <TableCell sx={{ 
                                                color: 'white',
                                                position: 'relative',
                                                padding: '2px 8px',
                                                fontSize: '0.85rem',
                                                borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                                            }}>
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center',
                                                    position: 'relative'
                                                }}>
                                                    {formatNumber(report.tq, 0)}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ 
                                                color: 'white',
                                                padding: '2px 8px',
                                                fontSize: '0.85rem',
                                                borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                                            }}>
                                                {formatNumber(report.taboola_clicks)}
                                            </TableCell>
                                            <TableCell sx={{ 
                                                color: 'white',
                                                padding: '2px 8px',
                                                fontSize: '0.85rem',
                                                borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                                            }}>
                                                {formatNumber(report.conversions)}
                                            </TableCell>
                                            <TableCell sx={{ 
                                                color: report.cvr < 10 ? '#f44336' : report.cvr < 20 ? '#ff9800' : '#4caf50',
                                                height: '100%',
                                                boxSizing: 'border-box',
                                                padding: '2px 8px',
                                                fontSize: '0.85rem',
                                                borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                                            }}>
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                                    {formatNumber(report.cvr, 2)}%
                                                    {report.cvr < 10 && (
                                                        <Tooltip title="CVR is below the recommended threshold (10%)">
                                                            <WarningIcon color="error" fontSize="small" sx={{ ml: 0.5, fontSize: '1rem' }} />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ 
                                                color: report.coverage < 80 ? '#f44336' : '#4caf50',
                                                height: '100%',
                                                boxSizing: 'border-box',
                                                padding: '2px 8px',
                                                fontSize: '0.85rem',
                                                borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                                            }}>
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                                    {formatNumber(report.coverage, 2)}%
                                                    {report.coverage < 80 && (
                                                        <Tooltip title="Coverage is below the recommended threshold (80%)">
                                                            <WarningIcon color="error" fontSize="small" sx={{ ml: 0.5, fontSize: '1rem' }} />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ 
                                                color: 'white',
                                                padding: '2px 8px',
                                                fontSize: '0.85rem',
                                                borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                                            }}>
                                                {formatCurrency(report.cost)}
                                            </TableCell>
                                            <TableCell sx={{ 
                                                color: 'white',
                                                padding: '2px 8px',
                                                fontSize: '0.85rem',
                                                borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                                            }}>
                                                {formatNumber(report.cpc, 2)}
                                            </TableCell>
                                            <TableCell sx={{ 
                                                color: 'white',
                                                padding: '2px 8px',
                                                fontSize: '0.85rem',
                                                borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                                            }}>
                                                {formatCurrency(report.revenue)}
                                            </TableCell>
                                            <TableCell sx={{ 
                                                color: (report.total_revenue / report.clicks) < 0.10 ? '#f44336' : (report.total_revenue / report.clicks) < 0.50 ? '#ff9800' : '#4caf50',
                                                fontWeight: 'bold',
                                                height: '100%',
                                                boxSizing: 'border-box',
                                                position: 'relative',
                                                padding: '2px 8px',
                                                fontSize: '0.85rem',
                                                borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                                            }}>
                                                <Box sx={{ 
                                                    display: 'inline-flex', 
                                                    alignItems: 'center',
                                                    position: 'relative'
                                                }}>
                                                    {/* Display the calculated payout value directly with 2 decimal places */}
                                                    {formatNumber(report.total_revenue / report.clicks, 2, '$')}
                                                    {(report.total_revenue / report.clicks) < 0.10 && report.clicks > 0 && (
                                                        <Tooltip title="Payout is critically low (below $0.10)">
                                                            <WarningIcon color="error" fontSize="small" sx={{ ml: 0.5, fontSize: '1rem' }} />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ 
                                                color: report.roas < 0.20 ? '#f44336' : report.roas < 1 ? '#ff9800' : report.roas < 1.5 ? '#ff9800' : '#4caf50',
                                                fontWeight: 'bold',
                                                height: '100%',
                                                boxSizing: 'border-box',
                                                position: 'relative',
                                                padding: '2px 8px',
                                                fontSize: '0.85rem',
                                                borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                                            }}>
                                                <Box sx={{ 
                                                    display: 'inline-flex', 
                                                    alignItems: 'center',
                                                    position: 'relative'
                                                }}>
                                                    {formatNumber(report.roas, 2)}
                                                    {report.roas < 0.20 && (
                                                        <Tooltip title="ROAS is critically low (below 0.20)">
                                                            <WarningIcon color="error" fontSize="small" sx={{ ml: 0.5, fontSize: '1rem' }} />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ 
                                                textAlign: 'center',
                                                padding: '2px 4px',
                                                fontSize: '0.85rem',
                                                borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                                            }}>
                                                {subId && (
                                                    <>
                                                        {blacklistStatuses[report.supply_feed_id] ? (
                                                        <Tooltip title={`Remove Supply Feed ID ${report.supply_feed_id} from blacklist for sub_id ${subId}`}>
                                                            <IconButton
                                                                size="small"
                                                                color="success"
                                                                onClick={() => handleOpenBlacklistDialog(report, 'remove')}
                                                                disabled={checkingBlacklist}
                                                                sx={{
                                                                    bgcolor: 'rgba(76, 175, 80, 0.1)',
                                                                    '&:hover': {
                                                                        bgcolor: 'rgba(76, 175, 80, 0.2)',
                                                                    },
                                                                    padding: '2px'
                                                                }}
                                                            >
                                                                <CheckCircleIcon sx={{ fontSize: '1.1rem' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title={`Blacklist Supply Feed ID ${report.supply_feed_id} for sub_id ${subId}`}>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleOpenBlacklistDialog(report, 'add')}
                                                                disabled={checkingBlacklist}
                                                                sx={{
                                                                    bgcolor: 'rgba(244, 67, 54, 0.1)',
                                                                    '&:hover': {
                                                                        bgcolor: 'rgba(244, 67, 54, 0.2)',
                                                                    },
                                                                    padding: '2px'
                                                                }}
                                                            >
                                                                <BlockIcon sx={{ fontSize: '1.1rem' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {blacklistStatuses[report.supply_feed_id] && (
                                                        <Chip
                                                            size="small"
                                                            label="BL"
                                                            sx={{
                                                                ml: 0.5,
                                                                bgcolor: 'rgba(0, 0, 0, 0.2)',
                                                                color: '#9e9e9e',
                                                                fontSize: '0.65rem',
                                                                height: '18px',
                                                                '& .MuiChip-label': { 
                                                                    padding: '0 4px',
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                    </>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
            
            {/* Summary section */}
            {!loading && !error && filteredReports.length > 0 && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ color: 'white' }}>
                            Summary
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CalendarTodayIcon sx={{ color: 'rgba(255,255,255,0.7)', mr: 1, fontSize: '0.875rem' }} />
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                {getDateRangeString()}
                            </Typography>
                        </Box>
                    </Box>
                    
                    {/* Debug logging for summary section */}
                    {console.debug('Summary Section Counts:', {
                        totalFeeds: filteredReports.length,
                        maxPayoutFeeds: filteredReports.filter(r => hasMaxPayout(r)).length,
                        goodPayoutFeeds: filteredReports.filter(r => {
                            // Use the same logic as in the table rows
                            const calculatedPayout = r.clicks > 0 ? r.total_revenue / r.clicks : 0;
                            return hasValidPayoutData && 
                                   r.clicks > 0 && 
                                   calculatedPayout >= payoutThreshold && 
                                   calculatedPayout >= 0.10 && 
                                   !hasMaxPayout(r);
                        }).length,
                        payoutThreshold,
                        hasValidPayoutData
                    })}
                    
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        {searchTerm.trim() 
                            ? `Showing ${filteredReports.length} of ${sortedReports.length} supply feeds.`
                            : `This campaign is running across ${sortedReports.length} supply feeds.`
                        }
                        {filteredReports.filter(r => r.roas >= 1).length > 0 && 
                            ` ${filteredReports.filter(r => r.roas >= 1).length} feeds have a positive ROAS (≥ 1).`
                        }
                        {!hasValidPayoutData && 
                            ` No valid payout data available (no clicks recorded).`
                        }
                        {filteredReports.filter(r => hasLowPerformance(r)).length > 0 && 
                            ` ${filteredReports.filter(r => hasLowPerformance(r)).length} feeds have performance issues.`
                        }
                        {Object.values(blacklistStatuses).filter(status => status).length > 0 && 
                            ` ${Object.values(blacklistStatuses).filter(status => status).length} feeds are blacklisted.`
                        }
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.7)' }}>
                        <strong>Recommendation:</strong> Consider optimizing high-performing feeds and pausing underperforming supply feeds.
                    </Typography>
                </Box>
            )}
            
            {/* Blacklist confirmation dialog */}
            <Dialog
                open={blacklistDialogOpen}
                onClose={handleCloseBlacklistDialog}
                aria-labelledby="blacklist-dialog-title"
                aria-describedby="blacklist-dialog-description"
                PaperProps={{
                    sx: {
                        bgcolor: '#1e2130',
                        color: 'white'
                    }
                }}
            >
                <DialogTitle id="blacklist-dialog-title">
                    {blacklistAction === 'add' ? 'Confirm Blacklist' : 'Confirm Remove from Blacklist'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="blacklist-dialog-description" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        {blacklistAction === 'add' ? (
                            <>
                                Are you sure you want to blacklist Supply Feed ID <strong>{feedToBlacklist?.supply_feed_id}</strong> for Sub ID <strong>{subId}</strong>?
                                <br /><br />
                                This will prevent this supply feed from being selected for this sub_id in the Traffic Mixer.
                            </>
                        ) : (
                            <>
                                Are you sure you want to remove Supply Feed ID <strong>{feedToBlacklist?.supply_feed_id}</strong> from the blacklist for Sub ID <strong>{subId}</strong>?
                                <br /><br />
                                This will allow this supply feed to be selected for this sub_id in the Traffic Mixer again.
                            </>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleCloseBlacklistDialog} 
                        color="primary"
                        disabled={blacklisting}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleBlacklistAction} 
                        color={blacklistAction === 'add' ? 'error' : 'success'} 
                        autoFocus
                        disabled={blacklisting}
                        startIcon={
                            blacklisting ? 
                                <CircularProgress size={20} color="inherit" /> : 
                                blacklistAction === 'add' ? 
                                    <BlockIcon /> : 
                                    <CheckCircleIcon />
                        }
                    >
                        {blacklisting ? 
                            (blacklistAction === 'add' ? 'Blacklisting...' : 'Removing...') : 
                            (blacklistAction === 'add' ? 'Blacklist' : 'Remove')}
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Notification snackbar */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={handleCloseNotification} 
                    severity={notification.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CampaignDetailAnalysis;